import axios from 'axios';
import { isEmpty, isNil, isObject } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, notification } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { isInstagramChannel } from 'utils/channels';
import { __t, applyTranslations } from 'utils/language';
import { getItem, setItem } from 'utils/storage/sessionStorage';
import { getProfileStatus } from '../../utils/discovery';
import {
  DEFAULT_FILTERS,
  audience as audienceInstagram,
  audienceTikTok
} from '../utils/filters';
import {
  DEFAULT_SORT_OPTION,
  HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME,
  LIGHTWEIGHT_CHECKBOX_STORAGE_NAME,
  SEARCH_BY_NAME_STORAGE_NAME,
  TAXONOMY_BLACKLIST,
  addDefaultInstagramFilters,
  getCategoriesFilter,
  getChannel,
  getSelectedChannels,
  getSortValue,
  getTaxonomies,
  handleStorageValue,
  isChannelChecked
} from '../utils/creators';

import { buildQueryObjectFromFilters, buildQueryStringFromQueryObject } from '../utils/queries';

import type {
  Action,
  DataStructureCommon,
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilterContent,
  DiscoveryCreatorsFilterDetails,
  DiscoveryCreatorsFilters,
  DiscoveryCreatorsQueryObject,
  DiscoveryCreatorsResult,
  DiscoveryCreatorsState,
  DiscoveryFilterOptions,
  DiscoveryParams,
  DiscoveryScreenState,
  Dispatch,
  GetState,
  PaginationLinks,
  PaginationMeta,
  ThunkAction
} from 'types';

applyTranslations(require('./creators.i18n.json'));

const { DISCOVERY } = API.END_POINTS;

const CREATORS_LOADING = 'discovery/creators/CREATORS_LOADING';
const CREATORS_LOADED = 'discovery/creators/CREATORS_LOADED';
const TAXONOMIES_LOADING = 'discovery/creators/TAXONOMIES_LOADING';
const TAXONOMIES_LOADED = 'discovery/creators/TAXONOMIES_LOADED';
const CREATORS_RESET = 'discovery/creators/CREATORS_RESET';
const FILTERS_UPDATED = 'discovery/creators/FILTERS_UPDATED';
const FILTERS_RESET = 'discovery/creators/FILTERS_RESET';
const PROFILE_DELETED = 'discovery/creators/PROFILE_DELETED';
const PAGINATION_UPDATED = 'discovery/creators/PAGINATION_UPDATED';
const SCREEN_STATE_UPDATED = 'discovery/creators/SCREEN_STATE';
const FILTERS_INSTAGRAM_RESET = 'discovery/creators/FILTERS_INSTAGRAM_RESET';
const FILTERS_TIKTOK_RESET = 'discovery/creators/FILTERS_TIKTOK_RESET';
const FILTERS_YOUTUBE_RESET = 'discovery/creators/FILTERS_YOUTUBE_RESET';
const CHANNELS_CLEAR = 'discovery/creators/CHANNELS_CLEAR';

const PAGINATION_LIMIT = 10;

const initialState = {
  defaultFilters: DEFAULT_FILTERS,
  filters: DEFAULT_FILTERS,
  results: [],
  taxonomies: {
    items: [],
    isLoading: false
  },
  isLoading: false,
  lastStateReset: Date.now(),
  screenState: {
    page: 0,
    sort: DEFAULT_SORT_OPTION,
    totalItems: 0,
    links: {},
    meta: {}
  }
};

let fetchCreatorsCancelToken;

const msgKey = 'discovery_creators_reducers';

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

const creatorsLoaded = (creators: Array<DiscoveryCreatorsResult>): Action => ({
  type: CREATORS_LOADED,

  payload: {
    creators
  }
});

const taxonomiesLoaded = (taxonomies: DiscoveryCreatorsFilterContent): Action => ({
  type: TAXONOMIES_LOADED,

  payload: {
    taxonomies
  }
});

const filtersUpdated = (filters: DiscoveryCreatorsFilters): Action => ({
  type: FILTERS_UPDATED,

  payload: {
    filters
  }
});

const paginationUpdated = (meta: PaginationMeta, links: PaginationLinks): Action => ({
  type: PAGINATION_UPDATED,

  payload: {
    meta,
    links
  }
});

const screenStateUpdated = (updatedScreenState: DiscoveryScreenState): Action => ({
  type: SCREEN_STATE_UPDATED,

  payload: {
    screenState: updatedScreenState
  }
});

export const deleteProfileViaSearch = (profileId: number): Action => ({
  type: PROFILE_DELETED,

  payload: {
    profileId
  }
});

export const clearAllChannelCheckboxes = (): Action => ({
  type: CHANNELS_CLEAR
});

export const resetDiscoveryCreators = (): Action => ({
  type: CREATORS_RESET
});

export const resetFilters = (): Action => ({
  type: FILTERS_RESET
});

export const resetFiltersInstagram = (): Action => ({
  type: FILTERS_INSTAGRAM_RESET
});

export const resetFiltersTiktok = (): Action => ({
  type: FILTERS_TIKTOK_RESET
});

export const resetFiltersYoutube = (): Action => ({
  type: FILTERS_YOUTUBE_RESET
});

const getSettings = (state: DiscoveryCreatorsState): DiscoveryCreatorsFilterDetails => ({
  screenState: state.screenState,
  sort: state.screenState.sort,
  filters: state.filters
});

const getUpdatedScreenState = (
  meta: PaginationMeta,
  links: PaginationLinks,
  sort: string
): DiscoveryScreenState => ({
  page: meta.currentPage || 0,
  sort: sort || DEFAULT_SORT_OPTION,
  totalItems: Number(meta.itemsTotal || 0),
  links,
  meta: { ...meta, itemsTotal: Number(meta.itemsTotal || 0) }
});

const updateFilters = (
  filters: DiscoveryCreatorsFilters,
  updatedFilter: DiscoveryCreatorsFilter
): DiscoveryCreatorsFilters => ({
  ...filters,
  ...updatedFilter
});

const handleSpecificFilterUpdates = (
  channels: Array<DataStructureCommon>,
  dispatch: Dispatch
): void => {
  const selectedChannels = getSelectedChannels(channels);
  const isInstagram = isChannelChecked(selectedChannels, 'instagram');
  const isTiktok = isChannelChecked(selectedChannels, 'tiktok');
  const isYoutube = isChannelChecked(selectedChannels, 'youtube');

  setItem(HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME, 'true');

  if (!isInstagram) {
    dispatch(resetFiltersInstagram());
  }

  if (!isTiktok) {
    dispatch(resetFiltersTiktok());
  }

  if (!isYoutube) {
    dispatch(resetFiltersYoutube());
  }
};

const handleChannelSelection = (
  dispatch: Dispatch
): void => {
  const hasManuallySelectedCheckboxes = getItem(HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME);

  if (isNil(hasManuallySelectedCheckboxes)) {
    dispatch(clearAllChannelCheckboxes());
  }
};

const getQueryString = (
  filters: DiscoveryCreatorsFilters,
  queryObject: Partial<DiscoveryCreatorsQueryObject>,
  params: DiscoveryParams,
  sort: string
): string => {
  const channel = getChannel(filters.channels);
  let queryString = buildQueryStringFromQueryObject(queryObject, params, `sort=${sort}`);

  if (isInstagramChannel(channel)) {
    queryString = addDefaultInstagramFilters(queryString);
  }

  return queryString;
};

const getQueryObject = (filters: DiscoveryCreatorsFilters): Partial<DiscoveryCreatorsQueryObject> => {
  const hasSearchByName = !isEmpty(filters.searchByName);

  return buildQueryObjectFromFilters(filters, hasSearchByName);
};

const triggerShowNotification = (
  filters: DiscoveryCreatorsFilters,
  dispatch: Dispatch
): void => {
  const profileStatus = getProfileStatus(filters.profiles);
  let showNotification = false;
  let msgPart = '';

  if (profileStatus.unverified) {
    showNotification = handleStorageValue(LIGHTWEIGHT_CHECKBOX_STORAGE_NAME);
    msgPart = __t(`${msgKey}.messages.for_lightweight_profiles`);
  }

  if (!isEmpty(filters.searchByName)) {
    showNotification = handleStorageValue(SEARCH_BY_NAME_STORAGE_NAME);
    msgPart = __t(`${msgKey}.messages.when_searching_by_name`);
  }

  if (showNotification) {
    dispatch(notification({
      type: 'info',
      message: __t(`${msgKey}.messages.filters_disabled`, {
        msgPart
      })
    }));
  }
};

const updatePagination = (
  meta: PaginationMeta,
  links: PaginationLinks
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(paginationUpdated(meta, links));
  }
);

export const updateScreenState = (screenState: DiscoveryScreenState): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(screenStateUpdated(screenState));
  }
);

const patchTicktokUrls = (creators: Array<DiscoveryCreatorsResult>): Array<DiscoveryCreatorsResult> => (
  creators.map(creator => ({
    ...creator,
    channels: {
      ...creator.channels,
      tiktok: !creator.channels.tiktok
        ? undefined
        : ({
          ...creator.channels.tiktok,
          urlRaw: creator.channels.tiktok?.url ?? '',
          url: (creator.channels.tiktok?.url ?? '')
            .replace(
              /^(https:\/\/www\.tiktok\.com\/)(share\/user\/\d+)$/,
              `$1@${creator.channels.tiktok?.username ?? ''}`
            )
        })
    }
  }))
);

export const fetchDiscoveryCreators = (
  options: DiscoveryFilterOptions = { reset: false }
): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    if (fetchCreatorsCancelToken) {
      fetchCreatorsCancelToken.cancel();
    }

    fetchCreatorsCancelToken = axios.CancelToken.source();

    dispatch(resetDiscoveryCreators());
    dispatch(setLoadingStatus(CREATORS_LOADING, true));

    const currentAppState = getState();
    const orgId = currentAppState.organizations?.currentOrganization?.id;
    const state = options.reset ? initialState : currentAppState.discoveryCreators;
    const settings = getSettings(state);
    const sort = settings.sort;
    const currentPage = options.page || 1;
    const filters = settings.filters;

    const params = {
      page: currentPage,
      items_per_page: PAGINATION_LIMIT,
      organization_id: orgId
    };

    const queryObject = getQueryObject(filters);
    const queryString = getQueryString(filters, queryObject, params, sort);

    // show a notification if lightweight checkbox is checked
    triggerShowNotification(filters, dispatch);

    try {
      const url = DISCOVERY.creators.getCreators(queryString);
      const response = await get(url, {
        cancelToken: fetchCreatorsCancelToken.token
      });
      const results = patchTicktokUrls(response.data ? response.data.data : []);
      const meta = response.data ? response.data.meta : {};
      const links = response.data ? response.data.links : {};
      const updatedScreenState = getUpdatedScreenState(meta, links, sort);

      dispatch(setLoadingStatus(CREATORS_LOADING, false));
      dispatch(updatePagination(meta, links));
      dispatch(updateScreenState(updatedScreenState));

      return dispatch(creatorsLoaded(results));
    } catch (error) {
      if (axios.isCancel(error)) {
        return; // eslint-disable-line consistent-return
      }

      dispatch(setLoadingStatus(CREATORS_LOADING, false));

      throw error;
    }
  }
);

export const fetchDiscoveryTaxonomies = (): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    dispatch(setLoadingStatus(TAXONOMIES_LOADING, true));

    try {
      const url = DISCOVERY.creators.taxonomies();
      const response = await get(url);
      const taxonomies = response.data || [];
      const currentDiscoveryCreators = getState().discoveryCreators;
      const settings = getSettings(currentDiscoveryCreators);

      const categories = getTaxonomies(taxonomies, 'specialized_in', TAXONOMY_BLACKLIST);
      const audienceInterests = getTaxonomies(taxonomies, 'specialized_in');

      const updatedFilter = {
        categories: getCategoriesFilter(categories, settings.filters.categories.matchMode),
        audienceInterests
      };

      const updatedFilters = updateFilters(settings.filters, updatedFilter);

      dispatch(setLoadingStatus(TAXONOMIES_LOADING, false));
      dispatch(filtersUpdated(updatedFilters));

      return dispatch(taxonomiesLoaded({ categories }));
    } catch (error) {
      dispatch(setLoadingStatus(TAXONOMIES_LOADING, false));

      if (axios.isCancel(error)) {
        return; // eslint-disable-line consistent-return
      }

      throw error;
    }
  }
);

export const updateDiscoveryCreatorsFilters = (
  updatedFilter: Partial<DiscoveryCreatorsFilters>
): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    const settings = getSettings(getState().discoveryCreators);

    if (isNil(updatedFilter) || !isObject(updatedFilter)) {
      return null;
    }

    const updatedFilterKey = Object.keys(updatedFilter)[0];
    const updatedFilters = updateFilters(settings.filters, updatedFilter);
    const sortValue = getSortValue(updatedFilters, updatedFilterKey, settings.sort);

    const updatedScreenState = {
      ...settings.screenState,
      sort: sortValue,
      totalItems: 0
    };

    dispatch(updateScreenState(updatedScreenState));
    dispatch(filtersUpdated(updatedFilters));

    // channel-specific filters should be reset under certain conditions
    if (updatedFilterKey === 'channels' && !isNil(updatedFilter.channels)) {
      handleSpecificFilterUpdates(updatedFilter.channels, dispatch);
    }

    // all channels should be deselected if searching by name *prior* to manually selecting channels
    if (updatedFilterKey === 'searchByName') {
      handleChannelSelection(dispatch);
    }

    return dispatch(fetchDiscoveryCreators());
  }
);

export const resetDiscoveryCreatorsFilters = (): ThunkAction => (
  (dispatch: Dispatch) => Promise.all([
    dispatch(resetFilters()),
    dispatch(fetchDiscoveryTaxonomies()),
    dispatch(fetchDiscoveryCreators())
  ])
);

export default createReducer<any, any>('discoveryCreators', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [FILTERS_RESET]: (state) => ({
    ...state,
    filters: initialState.defaultFilters,
    screenState: initialState.screenState,
    lastStateReset: Date.now()
  }),
  [FILTERS_UPDATED]: (state, { filters }) => ({
    ...state,
    filters
  }),
  [PAGINATION_UPDATED]: (state, { meta, links }) => ({
    ...state,
    screenState: {
      ...state.screenState,
      meta,
      links
    }
  }),
  [SCREEN_STATE_UPDATED]: (state, { screenState }) => ({
    ...state,
    screenState
  }),
  [CREATORS_RESET]: (state) => ({
    ...state,
    results: []
  }),
  [CREATORS_LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [CREATORS_LOADED]: (state, { creators }) => ({
    ...state,
    results: creators,
    isLoading: false
  }),
  [TAXONOMIES_LOADING]: (state, { status }) => ({
    ...state,
    taxonomies: {
      ...state.taxonomies,
      isLoading: status
    }
  }),
  [TAXONOMIES_LOADED]: (state, { taxonomies }) => ({
    ...state,
    taxonomies: {
      ...state.taxonomies,
      items: taxonomies,
      isLoading: false
    }
  }),
  [PROFILE_DELETED]: (state, { profileId }) => ({
    ...state,
    results: state.results.filter((i: DiscoveryCreatorsResult) => i.id !== profileId),
    screenState: {
      ...state.screenState,
      totalItems: Math.max(0, (state.screenState.totalItems || 0) - 1)
    }
  }),

  // channel-specific filter reset
  [FILTERS_INSTAGRAM_RESET]: (state) => ({
    ...state,
    filters: {
      ...state.filters,
      ...audienceInstagram,
      engagement: {
        ...state.filters.engagement,
        instagram: DEFAULT_FILTERS.engagement.instagram
      },
      followers: {
        ...state.filters.followers,
        instagram: DEFAULT_FILTERS.followers.instagram
      }
    },
    screenState: {
      ...state.screenState,
      sort: !isEmpty(state.filters.audienceCountriesTikTok) ? state.screenState.sort : DEFAULT_SORT_OPTION
    }
  }),
  [FILTERS_TIKTOK_RESET]: (state) => ({
    ...state,
    filters: {
      ...state.filters,
      ...audienceTikTok,
      engagement: {
        ...state.filters.engagement,
        tiktok: DEFAULT_FILTERS.engagement.tiktok
      },
      followers: {
        ...state.filters.followers,
        tiktok: DEFAULT_FILTERS.followers.tiktok
      },
      views: {
        ...state.filters.views,
        tiktok: DEFAULT_FILTERS.views.tiktok
      }
    },
    screenState: {
      ...state.screenState,
      sort: !isEmpty(state.filters.audienceCountries) ? state.screenState.sort : DEFAULT_SORT_OPTION
    }
  }),
  [FILTERS_YOUTUBE_RESET]: (state) => ({
    ...state,
    filters: {
      ...state.filters,
      engagement: {
        ...state.filters.engagement,
        youtube: DEFAULT_FILTERS.engagement.youtube
      },
      followers: {
        ...state.filters.followers,
        youtube: DEFAULT_FILTERS.followers.youtube
      },
      views: {
        ...state.filters.views,
        youtube: DEFAULT_FILTERS.views.youtube
      }
    }
  }),
  [CHANNELS_CLEAR]: (state) => ({
    ...state,
    filters: {
      ...state.filters,
      channels: state.filters.channels.map(channel => ({
        ...channel,
        selected: false
      }))
    }
  })
});
